import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { StaffMember, AuditLogItem, StaffRole, StaffStatus } from '../types';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import {
  Shield,
  UserPlus,
  Users,
  History,
  CheckCircle,
  AlertTriangle,
  Lock,
  Trash2,
  RefreshCw,
  Server,
  Cloud,
} from 'lucide-react';
import { formatDate } from '../lib/utils';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

export const SettingsPage: React.FC = () => {
  const { showToast } = useToast();
  const { user, permissions } = useAuth();

  const [activeTab, setActiveTab] = useState<'staff' | 'audit' | 'system'>('staff');
  const [staffList, setStaffList] = useState<StaffMember[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Add staff modal
  const [isAddStaffOpen, setIsAddStaffOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState<StaffRole>('marketing_staff');
  const [isSavingStaff, setIsSavingStaff] = useState(false);

  // Delete staff modal
  const [deleteStaffTarget, setDeleteStaffTarget] = useState<StaffMember | null>(null);

  const fetchStaff = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{ items: StaffMember[] }>('/staff');
      setStaffList(data.items || []);
    } catch (err: any) {
      if (err.status !== 403) {
        showToast('Staff Load Error', err.message, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      setIsLoading(true);
      const data = await api.get<{ items: AuditLogItem[] }>('/audit-logs');
      setAuditLogs(data.items || []);
    } catch (err: any) {
      showToast('Audit Log Error', err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'staff') fetchStaff();
    if (activeTab === 'audit') fetchAuditLogs();
  }, [activeTab]);

  const handleAddStaff = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim()) {
      showToast('Missing Fields', 'Email and name are required', 'error');
      return;
    }

    try {
      setIsSavingStaff(true);
      await api.post('/staff', {
        email: newEmail.trim().toLowerCase(),
        name: newName.trim(),
        role: newRole,
      });

      showToast('Staff Authorized', `${newName} has been added to the allowlist`, 'success');
      setIsAddStaffOpen(false);
      setNewEmail('');
      setNewName('');
      fetchStaff();
    } catch (err: any) {
      showToast('Failed to Add Staff', err.message, 'error');
    } finally {
      setIsSavingStaff(false);
    }
  };

  const handleUpdateRole = async (staffId: string, role: StaffRole, status: StaffStatus) => {
    try {
      await api.patch(`/staff/${staffId}`, { role, status });
      showToast('Staff Updated', 'Permissions updated successfully', 'success');
      fetchStaff();
    } catch (err: any) {
      showToast('Update Failed', err.message, 'error');
    }
  };

  const handleDeleteStaff = async () => {
    if (!deleteStaffTarget) return;
    try {
      await api.delete(`/staff/${deleteStaffTarget.id}`);
      showToast('Staff Removed', `${deleteStaffTarget.email} removed from allowlist`, 'info');
      setDeleteStaffTarget(null);
      fetchStaff();
    } catch (err: any) {
      showToast('Removal Failed', err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300 pb-12">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-extrabold text-white tracking-tight">
          System Settings & Governance
        </h1>
        <p className="text-xs text-slate-400 mt-1">
          Manage authorized marketing personnel, role-based access control, and audit logs.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-slate-800">
        <button
          onClick={() => setActiveTab('staff')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer ${
            activeTab === 'staff'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>Staff Allowlist</span>
        </button>

        <button
          onClick={() => setActiveTab('audit')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer ${
            activeTab === 'audit'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          <History className="w-4 h-4" />
          <span>Audit Trail</span>
        </button>

        <button
          onClick={() => setActiveTab('system')}
          className={`flex items-center gap-2 pb-3 px-1 text-sm font-semibold transition-colors border-b-2 cursor-pointer ${
            activeTab === 'system'
              ? 'border-orange-500 text-orange-400'
              : 'border-transparent text-slate-400 hover:text-slate-200'
          }`}
        >
          <Server className="w-4 h-4" />
          <span>Infrastructure</span>
        </button>
      </div>

      {/* Tab: Staff Allowlist */}
      {activeTab === 'staff' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              Only employees with emails listed below can log in via Cloudflare Access.
            </p>

            {permissions.canManageStaff && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsAddStaffOpen(true)}
                leftIcon={<UserPlus className="w-4 h-4" />}
              >
                Add Staff Member
              </Button>
            )}
          </div>

          <Card className="p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                    <th className="py-3 px-4">Staff Member</th>
                    <th className="py-3 px-4">Role</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Added On</th>
                    {permissions.canManageStaff && <th className="py-3 px-4 text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {staffList.map((member) => (
                    <tr key={member.id} className="hover:bg-slate-900/40">
                      <td className="py-3 px-4">
                        <p className="font-semibold text-slate-200">{member.name}</p>
                        <p className="text-slate-400 font-mono text-[11px]">{member.email}</p>
                      </td>

                      <td className="py-3 px-4">
                        {permissions.canManageStaff && member.id !== user?.id ? (
                          <select
                            value={member.role}
                            onChange={(e) =>
                              handleUpdateRole(member.id, e.target.value as StaffRole, member.status)
                            }
                            className="bg-slate-900 text-slate-200 border border-slate-700 rounded-lg px-2 py-1 text-xs focus:outline-none cursor-pointer"
                          >
                            <option value="marketing_staff">Marketing Staff</option>
                            <option value="admin">Administrator</option>
                          </select>
                        ) : (
                          <Badge variant={member.role === 'admin' ? 'primary' : 'neutral'}>
                            {member.role.replace('_', ' ')}
                          </Badge>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <Badge variant={member.status === 'active' ? 'active' : 'disabled'}>
                          {member.status}
                        </Badge>
                      </td>

                      <td className="py-3 px-4 text-slate-400 font-mono text-[11px]">
                        {formatDate(member.created_at)}
                      </td>

                      {permissions.canManageStaff && (
                        <td className="py-3 px-4 text-right">
                          {member.id !== user?.id ? (
                            <button
                              onClick={() => setDeleteStaffTarget(member)}
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-slate-800 transition-colors cursor-pointer"
                              title="Remove staff member"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <span className="text-slate-600 text-[10px]">Your Account</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Audit Trail */}
      {activeTab === 'audit' && (
        <Card className="p-0 overflow-hidden">
          <CardHeader className="p-4 border-b border-slate-800">
            <div>
              <CardTitle>Administrative Action Logs</CardTitle>
              <CardDescription>
                Immutable record of all link creations, destination edits, status changes, and staff updates
              </CardDescription>
            </div>
          </CardHeader>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="bg-slate-900/60 border-b border-slate-800 text-slate-400 font-semibold uppercase tracking-wider">
                  <th className="py-3 px-4">Timestamp</th>
                  <th className="py-3 px-4">Actor</th>
                  <th className="py-3 px-4">Action</th>
                  <th className="py-3 px-4">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {auditLogs.length > 0 ? (
                  auditLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-900/40 font-mono text-[11px]">
                      <td className="py-3 px-4 text-slate-400 whitespace-nowrap">
                        {formatDate(log.created_at, true)}
                      </td>
                      <td className="py-3 px-4 text-orange-400 font-semibold">{log.actor_email}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-200">
                          {log.action}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-300 font-sans text-xs">
                        {log.change_summary || '—'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-slate-500">
                      No audit events recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Tab: System & Infrastructure */}
      {activeTab === 'system' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="w-4 h-4 text-orange-400" />
                  <span>Cloudflare Production Configuration</span>
                </CardTitle>
                <CardDescription>Zero-server edge deployment status</CardDescription>
              </div>
            </CardHeader>

            <div className="space-y-3 text-xs">
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Target Production Domain</span>
                <span className="font-mono font-bold text-orange-400">https://lnk.brototype.com</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Database Engine</span>
                <span className="font-mono text-emerald-400">Cloudflare D1 (SQLite)</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Authentication Layer</span>
                <span className="font-mono text-emerald-400">Cloudflare Access (JWT)</span>
              </div>
              <div className="flex items-center justify-between p-2.5 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Redirect Engine Status</span>
                <span className="font-mono text-emerald-400">HTTP 302 Native Worker</span>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-orange-400" />
                  <span>Security & Boundary Enforcement</span>
                </CardTitle>
                <CardDescription>Policy guidelines for Brototype operations</CardDescription>
              </div>
            </CardHeader>

            <div className="space-y-3 text-xs text-slate-300 leading-relaxed">
              <p>
                &bull; <strong>Server-side claim verification:</strong> All management APIs verify the <code className="text-orange-400 font-mono">Cf-Access-Jwt-Assertion</code> cryptographic signature from Cloudflare Access.
              </p>
              <p>
                &bull; <strong>Public redirect isolation:</strong> Public visitors on <code className="text-orange-400 font-mono">/:shortCode</code> never require authentication and execute in under 10ms.
              </p>
              <p>
                &bull; <strong>Non-PII click tracking:</strong> IP addresses and intrusive fingerprints are never stored in D1 to maintain strict compliance and privacy.
              </p>
            </div>
          </Card>
        </div>
      )}

      {/* Add Staff Member Modal */}
      <Modal
        isOpen={isAddStaffOpen}
        onClose={() => setIsAddStaffOpen(false)}
        title="Add Staff Member to Allowlist"
        description="Grants dashboard and API access to authorized Brototype staff"
        maxWidth="md"
      >
        <form onSubmit={handleAddStaff} className="space-y-4">
          <Input
            label="Corporate Email Address"
            required
            type="email"
            placeholder="colleague@brototype.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />

          <Input
            label="Full Name"
            required
            placeholder="e.g. John Doe"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <div className="space-y-1.5">
            <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider">
              Role & Permissions
            </label>
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as StaffRole)}
              className="w-full px-3.5 py-2 text-sm bg-slate-900 text-slate-200 border border-slate-700 rounded-xl focus:outline-none focus:border-orange-500/80 cursor-pointer"
            >
              <option value="marketing_staff">Marketing Staff (Create & edit links, campaigns, analytics)</option>
              <option value="admin">Administrator (Full control, manage staff permissions, delete links)</option>
            </select>
          </div>

          <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-slate-800">
            <Button type="button" variant="ghost" size="sm" onClick={() => setIsAddStaffOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" isLoading={isSavingStaff}>
              Authorize Employee
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Staff Confirmation Modal */}
      {deleteStaffTarget && (
        <Modal
          isOpen={!!deleteStaffTarget}
          onClose={() => setDeleteStaffTarget(null)}
          title="Remove Staff Access"
          description={`Revoking access for: ${deleteStaffTarget.email}`}
          maxWidth="sm"
        >
          <div className="space-y-4">
            <p className="text-xs text-slate-300 leading-relaxed">
              This will remove {deleteStaffTarget.name} from the active staff allowlist. They will no longer be able to log in to the Link Manager dashboard.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => setDeleteStaffTarget(null)}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={handleDeleteStaff}>
                Revoke Access
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
